import { create } from "zustand";

const STORAGE_KEY = "cleo:thread-error-dismissals:v1";
export const DEFAULT_THREAD_ERROR = "The last turn failed.";
type Dismissals = Readonly<Record<string, string>>;

// One receipt per person's error source, identifying the exact failure occurrence.
// This is display state; the provider error and conversation remain unchanged.
export function getThreadErrorBannerKey(
  threadKey: string,
  error: string | null,
  occurrence: string | null = null,
  scopeKey = "local",
): string | null {
  return error === null
    ? null
    : `${JSON.stringify([scopeKey, threadKey])}\u0000${JSON.stringify([occurrence, error])}`;
}

function threadKeyOf(bannerKey: string): string {
  return bannerKey.slice(0, bannerKey.indexOf("\u0000"));
}

function restore(): Dismissals {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
    if (saved === null || typeof saved !== "object" || Array.isArray(saved)) return {};
    return Object.fromEntries(
      Object.entries(saved).filter(
        ([key, value]) =>
          typeof value === "string" && value.includes("\u0000") && threadKeyOf(value) === key,
      ),
    );
  } catch {
    return {};
  }
}

export const useThreadErrorDismissals = create<{ byThread: Dismissals }>(() => ({
  byThread: restore(),
}));
useThreadErrorDismissals.subscribe(({ byThread }, previous) => {
  if (byThread === previous.byThread) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(byThread));
  } catch {
    // Clearing remains usable in memory when local storage is unavailable.
  }
});

if (typeof window !== "undefined") {
  const acceptStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      useThreadErrorDismissals.setState({ byThread: restore() });
    }
  };
  window.addEventListener("storage", acceptStorage);
  import.meta.hot?.dispose(() => window.removeEventListener("storage", acceptStorage));
}

export function dismissThreadErrorBannerForSession(bannerKey: string | null): void {
  if (bannerKey === null) return;
  useThreadErrorDismissals.setState(({ byThread }) => ({
    byThread: { ...byThread, [threadKeyOf(bannerKey)]: bannerKey },
  }));
}

export function clearThreadErrorDismissal(threadKey: string, scopeKey: string): void {
  const key = JSON.stringify([scopeKey, threadKey]);
  useThreadErrorDismissals.setState(({ byThread }) => {
    if (!(key in byThread)) return { byThread };
    const next = { ...byThread };
    delete next[key];
    return { byThread: next };
  });
}

export function isThreadErrorBannerDismissedForSession(
  bannerKey: string | null,
  byThread = useThreadErrorDismissals.getState().byThread,
): boolean {
  return bannerKey !== null && byThread[threadKeyOf(bannerKey)] === bannerKey;
}
